import { AlertTriangle } from 'lucide-react';
import { PROJECT_RISK_LABELS } from '@epasu/core/constants';
import type { ProjectRisk } from '@epasu/core/constants';

/**
 * Warum ein Projekt gerade Aufmerksamkeit braucht.
 *
 * Das ist der Teil, den ein Projektwerkzeug tatsächlich leistet: anstupsen,
 * bevor es der Kunde tut. Die Gründe stehen ausgeschrieben da, nicht als
 * rotes Symbol — „Phase überfällig“ sagt, was zu tun ist, ein Ausrufezeichen
 * nicht.
 *
 * Nichts anzeigen, wenn nichts anliegt: ein dauerhaft sichtbarer Kasten
 * „alles in Ordnung“ wird nach drei Tagen übersehen und nimmt der Warnung
 * ihre Wirkung.
 */
export function ProjectRiskBand({ risks }: { risks: ProjectRisk[] }) {
  if (risks.length === 0) return null;

  return (
    <div className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/5 px-3 py-2">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
      <p className="text-sm text-danger">
        {risks.map((r) => PROJECT_RISK_LABELS[r]).join(' · ')}
      </p>
    </div>
  );
}

/**
 * Fortschritt als schmaler Balken.
 *
 * `null` zeigt nichts — ein Projekt ohne Aufgaben und ohne Phasen hat keinen
 * Fortschritt von null Prozent, es hat keinen.
 */
export function ProjectProgress({
  progress,
  className = '',
}: {
  progress: number | null;
  className?: string;
}) {
  if (progress === null) return null;
  const prozent = Math.round(progress * 100);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div
        className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={prozent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Fortschritt"
      >
        <div
          className={`h-full rounded-full ${prozent === 100 ? 'bg-emerald-500' : 'bg-primary'}`}
          style={{ width: `${prozent}%` }}
        />
      </div>
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {prozent}%
      </span>
    </div>
  );
}
